import { type ReactNode } from "react";
import { ChevronIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  Card,
  CardDescription,
  CardTitle,
} from "@vega-core/components/shadcn/card";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

type Props = {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
  variant?: "default" | "hoverArrow" | "iconBg";
  onClick?: VoidFunction;
  /** Called when the arrow button is clicked (hoverArrow variant). */
  onArrowClick?: VoidFunction;
  buttonText?: string;
  buttonPosition?: "center" | "end" | "start";
  fullContentWidth?: boolean;
};

export const NavigationCard = ({
  icon,
  title,
  description,
  onClick,
  onArrowClick,
  className,
  variant = "default",
  buttonText,
  fullContentWidth,
  buttonPosition = "end",
}: Props) => {
  const isHoverArrow = variant === "hoverArrow";
  const cardClickHandler = isHoverArrow ? onArrowClick : onClick;

  return (
    <Card
      className={cnTwMerge(
        "flex items-center justify-between shadow-none gap-4 p-3 transition-all",
        className,
        {
          "group hover:bg-background/tertiary/40 hover:border-text/primary":
            isHoverArrow,
          "cursor-pointer": Boolean(cardClickHandler),
          "hover:bg-background/tertiary": onClick && !isHoverArrow,
        },
      )}
      onClick={cardClickHandler}
    >
      <div className="flex items-center gap-4">
        {icon ? (
          <div
            className={cnTwMerge("flex items-center justify-center", {
              "rounded-lg bg-text/secondary/10 p-3 h-13 w-13":
                variant === "iconBg",
              "self-start": variant === "hoverArrow",
            })}
          >
            {icon}
          </div>
        ) : null}
        <div
          className={cnTwMerge("flex flex-col gap-1", {
            "max-w-xs": !fullContentWidth,
          })}
        >
          <CardTitle
            className={cnTwMerge({
              "body-semi-large-medium": variant === "hoverArrow",
            })}
          >
            {title}
          </CardTitle>
          {description ? (
            <CardDescription>{description}</CardDescription>
          ) : null}
        </div>
      </div>

      {variant === "hoverArrow" ? (
        <div
          className={cnTwMerge(
            "opacity-0 transition-opacity group-hover:opacity-100 self-end",
            {
              "self-center": buttonPosition === "center",
              "self-start": buttonPosition === "start",
              "self-end": buttonPosition === "end",
            },
          )}
        >
          <ThemedButton
            className="hover:bg-background/tertiary "
            dataTestId="navigation-card-chevron-icon"
            icon={
              <ChevronIcon className="h-6 w-6 text-text/primary rotate-270" />
            }
            iconPosition="right"
            onClick={(event) => {
              event.stopPropagation();
              onArrowClick?.();
            }}
            variant="secondary"
          >
            {buttonText}
          </ThemedButton>
        </div>
      ) : (
        <ChevronIcon
          className="h-6 w-6 text-text/secondary rotate-270"
          data-testid="navigation-card-chevron"
        />
      )}
    </Card>
  );
};
